import React, { FC } from "react";
import { useReturnUrl } from "shared-hooks";
import { useHistory } from "react-router-dom";
import { SubscriptionActions } from "component-library";
import { checkIsCD } from "shared-auth";
import { ReactComponent as InfoIconSvg } from "component-library/src/images/svg/icons/warning-icon-base.svg";
import { SubscriptionCalculatedStatus } from "app/constants";
import { HtmlAnchorButton } from "pages/IndexPage/MyAccountPageUserMessaging";
import { IBillingApiResponse } from "services/api/BillingApi/BillingApi";
import checkIsNoActiveSubscription from "helpers/checkIsNoActiveSubscription";
import applicationMap, { CheckoutType } from "services/applicationMap";
import checkIsValidAddress from "helpers/checkIsValidAddress";
import { getCheckoutLink } from "helpers/getCheckoutLink";
import { useUpdateDetailsPopUp } from "app/hooks/useUpdateDetailsPopUp";
import NotificationBanner from "./NotificationBanner";

type NotificationBannerProps = {
  messageHtml: string;
  buttons: ReadonlyArray<HtmlAnchorButton>;
  performerSubscriptionData: IBillingApiResponse | undefined;
  isLoading: boolean;
  subActionsData?: SubscriptionActions;
};

const SubscriptionNotificationBanner: FC<NotificationBannerProps> = ({
  subActionsData,
  ...props
}) => {
  const history = useHistory();
  const { redirectWithReturnUrl } = useReturnUrl();

  const { address, account, subscription } =
    props.performerSubscriptionData || {};

  const isExpired =
    subscription?.calculatedStatus === SubscriptionCalculatedStatus.Expired;

  const noActiveSubscription = checkIsNoActiveSubscription({
    id: account?.id,
    subscription: subscription,
  });
  const isRenewalPeriod = subActionsData?.isEligibleToRenew;

  const isValidAddress = checkIsValidAddress(address);

  const updateMyDetailRedirect =
    !isValidAddress && (noActiveSubscription || isRenewalPeriod);

  const checkoutPageRedirect = isValidAddress && (isRenewalPeriod || isExpired);

  const handleRedirectUser = () => {
    const cdRedirect = applicationMap.routes.index();

    const targetLink = isExpired
      ? getCheckoutLink({
          planCode: subscription?.planCode,
          checkoutType: CheckoutType.Reactivation,
        })
      : getCheckoutLink({
          planCode: subscription?.planCode,
        });

    const returnPath = checkIsCD() ? cdRedirect : targetLink;

    if (updateMyDetailRedirect) {
      redirectWithReturnUrl(
        applicationMap.routes.settingsMyDetails(),
        returnPath
      );

      return;
    }

    if (checkoutPageRedirect) {
      history.push(targetLink);
    }
  };

  const { showUpdateDetailsPopUp } = useUpdateDetailsPopUp(handleRedirectUser);

  const handleReactivationRenewClick = () => {
    if (!isValidAddress) {
      return showUpdateDetailsPopUp();
    } else {
      return handleRedirectUser();
    }
  };

  let buttons = props.buttons;
  let messageHtml = props.messageHtml;

  if (isRenewalPeriod) {
    buttons = [
      {
        ...props.buttons[0],
        onClick: handleReactivationRenewClick,
      },
    ];
  } else if (isExpired) {
    buttons = [
      props.buttons[0],
      {
        ...props.buttons[1],
        onClick: handleReactivationRenewClick,
      },
    ];
  }

  if (props.isLoading) return null;

  return (
    <NotificationBanner
      messageHtml={messageHtml}
      buttons={buttons}
      icon={
        <InfoIconSvg
          width={24}
          height={24}
          className="mr-2 text-color-brand-one"
        />
      }
    />
  );
};

export default SubscriptionNotificationBanner;
